import { useAppRouteParams } from "@/AppRoutes";
import { getAPIErrorDetailOrMessage } from "@/api/helpers";
import { API_COSTS, getMaxConcurrentApiCount } from "@/api/rateLimit";
import { RenderUploadedRow } from "@/components/DragAndDropFields/helper";
import { useVersionCategories } from "@/components/InOrganizationProvider/VersionCategoriesProvider";
import { translation } from "@/i18n";
import { nsUpload } from "@/i18n/locales/upload";
import { nsVersion } from "@/i18n/locales/version";
import { Checkbox } from "@fluentui/react-components";
import React, { useState, useEffect } from "react";
import { Version } from "../../../../../domain/Version";
import {
  DragAndDropAcceptFileType,
  DragAndDropFields,
  DragAndDropUploadData,
  dragAndDropAcceptFiles,
} from "../../../../DragAndDropFields";
import type {
  DialogPhaseStatesType,
  DialogPhaseType,
} from "../../../helpers/hooks";
import { useDialog } from "../../../helpers/hooks";
import { StatusList, VersionUpdateStatus } from "./StatusList";
import styles from "./index.module.css";

type BulkUpdateBaseArgs = {
  versionId: string;
  organizationId: string;
  file: File;
};

type BulkUpdateKind = "sheetSchema" | "viewSetting";

type UpdateMode = "merge" | "replace";

type Props = {
  versions: Version[];
  selectedVersionIds: string[];
  setSelectedVersionIds: React.Dispatch<React.SetStateAction<string[]>>;
  kind: BulkUpdateKind;
  functionName: keyof typeof API_COSTS;
  updateFunction: (
    args: BulkUpdateBaseArgs & { updateMode: UpdateMode }
  ) => Promise<void>;
} & DialogPhaseStatesType;

const useTranslation = translation.use(nsVersion, nsUpload);

export const DialogBulkUpdate: React.FC<Props> = ({
  versions,
  selectedVersionIds,
  setSelectedVersionIds,
  kind,
  updateFunction,
  functionName,
  dialogPhase,
  setDialogPhase,
}) => {
  const { t } = useTranslation();
  const { organizationId } = useAppRouteParams();
  const { versionCategories } = useVersionCategories();
  const [file, setFile] = useState<File | null>(null);
  const [updateStatuses, setUpdateStatuses] = useState<VersionUpdateStatus[]>(
    []
  );
  const { renderDialogs } = useDialog({
    dialogPhase,
    setDialogPhase,
  });
  const [replaceConfirmDialogPhase, setReplaceConfirmDialogPhase] =
    useState<DialogPhaseType>(null);
  const { renderDialogs: renderReplaceConfirmDialog } = useDialog({
    dialogPhase: replaceConfirmDialogPhase,
    setDialogPhase: setReplaceConfirmDialogPhase,
  });
  const [uploadedFileList, setUploadedFileList] = useState<
    DragAndDropUploadData[]
  >([]);
  const [existingType, setExistingType] = useState<
    DragAndDropAcceptFileType | undefined
  >();

  const [selectedUpdateMode, setSelectedUpdateMode] =
    useState<UpdateMode>("merge");
  const targetResourceName = t.version.bulkUpdate.targetName[kind]();
  const replaceConfirmTitle = t.version.bulkUpdate.replaceConfirm.title[kind]();
  const replaceConfirmMessage =
    t.version.bulkUpdate.replaceConfirm.message[kind]();
  const replaceConfirmButtonText =
    t.version.bulkUpdate.replaceConfirm.button[kind]();

  useEffect(() => {
    setFile(uploadedFileList[0]?.file || null);
  }, [uploadedFileList]);

  const executeUpdate = async () => {
    if (!file) return;

    setUpdateStatuses(
      selectedVersionIds.map((id) => ({
        versionId: id,
        status: "waiting",
      }))
    );

    // ref: [レートリミット: どの1秒間(ウィンドウ)についても「ウィンドウのコスト」が6を超えないこと。](https://www.notion.so/algo-artis/SSUI-API-2025-07-07ver-184af951024b807897a6cf64f1f2fb91)
    // 任意の1秒間での累計コスト ≤ 6 を遵守するために、
    // maxConcurrentApiCount件ずつ並列実行し、バッチ間に1秒待機してAPIサーバーへの負荷を平準化します。
    const maxConcurrentApiCount = getMaxConcurrentApiCount(functionName);

    // バッチ処理でAPIを実行
    for (let i = 0; i < selectedVersionIds.length; i += maxConcurrentApiCount) {
      const batch = selectedVersionIds.slice(i, i + maxConcurrentApiCount);

      // 現在のバッチのAPIを並列実行
      const promises = batch.map(async (versionId) => {
        try {
          setUpdateStatuses((prev) =>
            prev.map((s) =>
              s.versionId === versionId ? { ...s, status: "in_progress" } : s
            )
          );

          await updateFunction({
            versionId,
            organizationId,
            file,
            updateMode: selectedUpdateMode,
          });

          setUpdateStatuses((prev) =>
            prev.map((s) =>
              s.versionId === versionId ? { ...s, status: "success" } : s
            )
          );
        } catch (error) {
          const { errorMessage } = getAPIErrorDetailOrMessage(error);
          setUpdateStatuses((prev) =>
            prev.map((s) =>
              s.versionId === versionId
                ? {
                    ...s,
                    status: "failed",
                    error: errorMessage,
                  }
                : s
            )
          );
        }
      });

      // 現在のバッチの全てのAPIのレスポンスを待つ
      await Promise.all(promises);

      // 最後のバッチ以外は1秒待機
      if (i + maxConcurrentApiCount < selectedVersionIds.length) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }
  };

  const handleConfirmClick = () => {
    if (selectedUpdateMode === "replace") {
      setReplaceConfirmDialogPhase("confirm");
      return;
    }
    void executeUpdate();
  };

  useEffect(() => {
    return () => {
      setUploadedFileList([]);
      setSelectedUpdateMode("merge");
    };
  }, [dialogPhase]);

  // 更新中および更新後はダイアログを閉じるまで操作を無効化
  const hasStartedUpdateStatus = updateStatuses.some(
    (s) => s.status !== "waiting"
  );

  return (
    <>
      {renderDialogs({
        confirm: {
          title: t.version.bulkUpdate.dialogTitle({
            targetName: targetResourceName,
          }),
          buttonText: t.version.bulkUpdate.button(),
          onClickCancel: () => {
            setDialogPhase(null);
            setFile(null);
            setUploadedFileList([]);
            setUpdateStatuses([]);
            setSelectedVersionIds([]);
          },
          onClick: handleConfirmClick,
          isDisableButton: !file || hasStartedUpdateStatus,
          element: (
            <div className={styles.dialogContent}>
              <div className={styles.dialogDescription}>
                <p>
                  {t.version.bulkUpdate.description({
                    count: selectedVersionIds.length,
                    targetName: targetResourceName,
                  })}
                </p>
                <p>{t.version.bulkUpdate.selectFile()}</p>
              </div>
              <div className={styles.dragAndDropWrapper}>
                <DragAndDropFields
                  accept={dragAndDropAcceptFiles.yaml}
                  uploadedFileList={uploadedFileList}
                  setUploadedFileList={setUploadedFileList}
                  existingType={existingType}
                  setExistingType={setExistingType}
                  fileTypeConfigs={[{ type: "yaml", limit: 1 }]}
                  extElement={
                    uploadedFileList.length > 0 ? (
                      <>
                        <div className={styles.subtitle}>
                          {t.upload.dataName()}
                        </div>
                        {uploadedFileList.map(
                          ({ file, id, isDisableDelete }) => {
                            return (
                              <div
                                className={styles.uploadedFileInfoBlock}
                                key={id}
                              >
                                <RenderUploadedRow
                                  key={id}
                                  file={file}
                                  id={id}
                                  isDisableDeleteButton={
                                    isDisableDelete || hasStartedUpdateStatus
                                  }
                                  onDelete={() => {
                                    setUploadedFileList((state) =>
                                      state.filter((v) => v.id !== id)
                                    );
                                  }}
                                />
                              </div>
                            );
                          }
                        )}
                        <>
                          <div className={styles.subtitle}>
                            {t.version.bulkUpdate.optionsLabel()}
                          </div>
                          <Checkbox
                            style={{
                              height: "32px",
                            }}
                            checked={selectedUpdateMode === "replace"}
                            disabled={hasStartedUpdateStatus}
                            onChange={(_, data) => {
                              const isReplace = data.checked === true;
                              setSelectedUpdateMode(
                                isReplace ? "replace" : "merge"
                              );
                            }}
                            label={t.version.bulkUpdate.allowDestructive[
                              kind
                            ]()}
                          />
                        </>
                      </>
                    ) : null
                  }
                />
              </div>
              <StatusList
                versions={versions}
                versionCategories={versionCategories ?? []}
                selectedVersionIds={selectedVersionIds}
                updateStatuses={updateStatuses}
              />
            </div>
          ),
        },
      })}
      {renderReplaceConfirmDialog({
        confirm: {
          title: replaceConfirmTitle,
          displayMessage: replaceConfirmMessage,
          buttonText: replaceConfirmButtonText,
          isThemeRed: true,
          onClick: () => {
            setReplaceConfirmDialogPhase(null);
            void executeUpdate();
          },
          onClickCancel: () => {
            setReplaceConfirmDialogPhase(null);
          },
        },
      })}
    </>
  );
};
